import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fmtINR, fmtNum, fmtDate } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { StatusBadge, Loading, SectionTitle } from '@/components/Shared';
import { ServiceReviewCard } from '@/components/ServiceReviewCard';
import { Fuel, Droplets, FlaskConical, RotateCw, MapPin, Receipt, LifeBuoy, Sparkles, Loader2 } from 'lucide-react';
import { logError } from '@/lib/log';

export default function CustomerHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [aiSug, setAiSug] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const getAiSuggestion = async () => {
    setAiBusy(true);
    try { const r = await api.get('/ai/reorder-suggestion'); setAiSug(r.data); }
    catch (e) { logError('AI suggestion failed', e); setAiSug({ has_history: false, message: 'AI suggestion unavailable right now.' }); }
    setAiBusy(false);
  };

  const load = useCallback(async () => {
    const [pricing, orders, invoices, profile] = await Promise.all([
      api.get('/pricing/current'),
      api.get('/orders/mine'),
      api.get('/invoices/mine'),
      api.get('/customers/me'),
    ]);
    setData({ pricing: pricing.data, orders: orders.data, invoices: invoices.data, profile: profile.data });
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;

  const diesel = data.pricing.find((p) => p.category === 'Diesel');
  const active = data.orders.find((o) => ['confirmed', 'driver_assigned', 'en_route', 'arrived', 'delivery_started'].includes(o.status));
  const recent = data.orders.slice(0, 4);
  const outstanding = data.invoices.filter((i) => i.balance > 0).reduce((s, i) => s + i.balance, 0);

  return (
    <div className="space-y-5">
      {/* Branded welcome */}
      <div className="flex flex-col items-center text-center pt-1" data-testid="customer-welcome-block">
        <img src="/assets/logo.png" alt="Mobile Fuel Kart" className="h-12" />
        <p className="text-sm text-muted-foreground mt-2">
          Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! What would you like to order today?
        </p>
      </div>

      {/* Product categories */}
      <div className="grid grid-cols-3 gap-3">
        <button onClick={() => nav('/app/order/diesel-hsd')}
          className="rounded-xl border border-border bg-card p-4 flex flex-col items-center gap-2 hover:border-primary hover:shadow-md transition-shadow"
          data-testid="category-diesel-card">
          <div className="h-12 w-12 rounded-full flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg,#0F4C81,#0F766E)' }}>
            <Fuel className="h-6 w-6" />
          </div>
          <span className="text-sm font-semibold">Diesel</span>
          <span className="text-[11px] text-muted-foreground leading-tight">Doorstep HSD delivery</span>
        </button>
        <button onClick={() => nav('/app/shop?cat=Lubricants')}
          className="rounded-xl border border-border bg-card p-4 flex flex-col items-center gap-2 hover:border-primary hover:shadow-md transition-shadow"
          data-testid="category-lube-card">
          <div className="h-12 w-12 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center">
            <Droplets className="h-6 w-6" />
          </div>
          <span className="text-sm font-semibold">Lube</span>
          <span className="text-[11px] text-muted-foreground leading-tight">Engine & gear oils</span>
        </button>
        <button onClick={() => nav('/app/shop?cat=DEF')}
          className="rounded-xl border border-border bg-card p-4 flex flex-col items-center gap-2 hover:border-primary hover:shadow-md transition-shadow"
          data-testid="category-def-card">
          <div className="h-12 w-12 rounded-full bg-sky-500/15 text-sky-600 dark:text-sky-400 flex items-center justify-center">
            <FlaskConical className="h-6 w-6" />
          </div>
          <span className="text-sm font-semibold">DEF</span>
          <span className="text-[11px] text-muted-foreground leading-tight">AdBlue & urea</span>
        </button>
      </div>

      <div className="rounded-xl p-5 text-white relative overflow-hidden" style={{ background: 'linear-gradient(120deg,#0F4C81,#0F766E)' }}>
        <div className="relative z-10">
          <p className="text-white/80 text-sm">Current Diesel Price</p>
          <div className="flex items-end gap-2"><span className="font-display text-4xl font-bold">{fmtINR(diesel?.price)}</span><span className="text-white/80 mb-1">/ litre</span></div>
          <p className="text-white/70 text-xs mt-1">No GST on diesel · Delivery charged separately (18% GST)</p>
          <Button onClick={() => nav('/app/order/diesel-hsd')} className="mt-4 bg-white text-primary hover:bg-white/90 h-12 font-semibold w-full sm:w-auto" data-testid="customer-home-order-diesel-button">
            <Fuel className="h-5 w-5 mr-2" /> Order Diesel Now
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <button onClick={() => recent[0] ? api.post(`/orders/${recent[0].id}/reorder`).then((r) => nav(`/app/track/${r.data.id}`)) : nav('/app/catalog')} className="rounded-xl border border-border bg-card p-3 flex flex-col items-center gap-1 hover:bg-muted" data-testid="quick-reorder-button">
          <RotateCw className="h-5 w-5 text-primary" /><span className="text-xs font-medium">Reorder</span>
        </button>
        <button onClick={() => nav('/app/invoices')} className="rounded-xl border border-border bg-card p-3 flex flex-col items-center gap-1 hover:bg-muted">
          <Receipt className="h-5 w-5 text-primary" /><span className="text-xs font-medium">Invoices</span>
        </button>
        <button onClick={() => nav('/app/profile')} className="rounded-xl border border-border bg-card p-3 flex flex-col items-center gap-1 hover:bg-muted">
          <LifeBuoy className="h-5 w-5 text-primary" /><span className="text-xs font-medium">Support</span>
        </button>
      </div>

      <div className="rounded-xl border border-border bg-card p-4" data-testid="ai-reorder-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><span className="text-sm font-semibold">Smart Reorder</span><span className="text-[10px] text-muted-foreground uppercase tracking-wide">AI</span></div>
          <Button size="sm" variant="secondary" onClick={getAiSuggestion} disabled={aiBusy} data-testid="ai-reorder-button">
            {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : aiSug ? 'Refresh' : 'Get suggestion'}
          </Button>
        </div>
        {!aiSug && !aiBusy && <p className="text-xs text-muted-foreground mt-1.5">Let AI analyse your usage and suggest your next refill quantity & date.</p>}
        {aiSug && !aiBusy && (aiSug.has_history ? (
          <div className="mt-2 space-y-1.5" data-testid="ai-reorder-result">
            <p className="text-sm font-medium">{aiSug.product_name} · {fmtNum(aiSug.suggested_qty)} L · by {aiSug.suggested_date}</p>
            <p className="text-xs text-muted-foreground">{aiSug.rationale}</p>
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" onClick={() => nav(`/app/order/${aiSug.product_id || 'diesel-hsd'}`)} data-testid="ai-reorder-order-now"><Fuel className="h-4 w-4 mr-1" />Order now</Button>
              <span className="text-[10px] text-muted-foreground capitalize">confidence: {aiSug.confidence}</span>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground mt-2">{aiSug.message}</p>
        ))}
      </div>

      {active && (
        <div className="rounded-xl border border-border bg-card p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => nav(`/app/track/${active.id}`)} data-testid="active-delivery-card">
          <div className="flex items-center justify-between"><span className="text-sm font-semibold">Active Delivery</span><StatusBadge status={active.status} label={active.status_label} /></div>
          <div className="mt-2 flex items-center justify-between">
            <div><p className="font-medium">{active.order_no}</p><p className="text-sm text-muted-foreground">{fmtNum(active.pricing.qty)} {active.pricing.unit} · {active.pricing.product_name}</p></div>
            <Button size="sm" variant="secondary"><MapPin className="h-4 w-4 mr-1" /> Track</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Outstanding</p>
          <p className="font-display text-2xl font-semibold" data-testid="outstanding-amount-value">{fmtINR(outstanding)}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Total Litres</p>
          <p className="font-display text-2xl font-semibold">{fmtNum(data.profile.analytics?.total_litres || 0)} L</p>
        </div>
      </div>

      <div>
        <SectionTitle action={<Button variant="ghost" size="sm" onClick={() => nav('/app/orders')}>View all</Button>}>Recent Orders</SectionTitle>
        <div className="space-y-2">
          {recent.length === 0 && <p className="text-sm text-muted-foreground">No orders yet.</p>}
          {recent.map((o) => (
            <div key={o.id} className="rounded-xl border border-border bg-card p-3 flex items-center justify-between cursor-pointer hover:bg-muted/50" onClick={() => nav(`/app/track/${o.id}`)} data-testid={`recent-order-${o.order_no}`}>
              <div><p className="font-medium text-sm">{o.order_no}</p><p className="text-xs text-muted-foreground">{fmtNum(o.pricing.qty)} {o.pricing.unit} · {fmtDate(o.created_at)}</p></div>
              <div className="flex items-center gap-2"><span className="text-sm font-semibold">{fmtINR(o.pricing.total)}</span><StatusBadge status={o.status} label={o.status_label} /></div>
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-xl"><ServiceReviewCard /></div>
    </div>
  );
}
